import { FRONTIER_BOUNDARY, FRONTIER_ROTATION } from './frontierTownModel';
import type { Map as MapboxMap, FilterSpecification } from 'mapbox-gl';
import { replacementBoundary, MODEL_ROTATION } from './apsHqPlacement';
import { COOK_BOUNDARY, COOK_ROTATION } from './cookMarketplaceModel';
import { FIFTEENTH_BOUNDARY, FIFTEENTH_ROTATION } from './fifteenthStreetModel';

export type SiteModelFocusRequest = { siteId: string; sequence: number };
export const SITE_MODEL_VIEWS: Record<string, { footprint: number[][]; bearing: number; height: number }> = {
  'jn7dhtzw2faqdyxm7aqmvzewyx7jcajd': { footprint: FRONTIER_BOUNDARY, bearing: FRONTIER_ROTATION, height: 14 },
  'jn78tpv4w48wzm570c0hm5c0d97jdyc8': { footprint: replacementBoundary.geometry.coordinates[0], bearing: MODEL_ROTATION, height: 10 },
  'jn7a68m10p83dmjzbtcg70a4957jd8h7': { footprint: COOK_BOUNDARY, bearing: COOK_ROTATION, height: 16 },
  'jn7debypz8v7k7j3ydh9bmf0jd7jdv2q': { footprint: FIFTEENTH_BOUNDARY, bearing: FIFTEENTH_ROTATION, height: 13 },
};

export function modeledSiteGeofenceFilter(satellite: boolean): FilterSpecification | null {
  return satellite ? null : ['!', ['in', ['get', 'siteId'], ['literal', Object.keys(SITE_MODEL_VIEWS)]]];
}

export function modelFocusPadding(width: number, height: number, coveredLeft: number) {
  const left = Math.min(Math.max(0, coveredLeft), Math.max(0, width - 120));
  const margin = Math.max(12, Math.min(48, (width - left) * 0.06, height * 0.07));
  return { left: left + margin, right: margin, top: Math.min(110, height * 0.22), bottom: margin };
}

export function focusSiteModel(map: MapboxMap, siteId: string, satellite: boolean, coveredLeft: number) {
  const view = SITE_MODEL_VIEWS[siteId];
  if (!view) return false;
  const container = map.getContainer();
  if (container.clientWidth < 120 || container.clientHeight < 120) return false;
  const pitch = satellite ? 0 : 50;
  // Include roof height in the ground envelope seen by the tilted camera. A
  // roof projects above its footprint; fitting only the parcel can crop it.
  const bearing = view.bearing * Math.PI / 180;
  const roofOffset = view.height * Math.tan(pitch * Math.PI / 180);
  const metresPerDegree = Math.PI * 6371008.8 / 180;
  const mercator = ([lng, lat]: number[]) => [(lng + 180) / 360,
    (1 - Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)) / Math.PI) / 2];
  const lngLat = ([x, y]: number[]): [number, number] => [x * 360 - 180,
    Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI];
  const anchor = mercator(view.footprint[0]);
  const c = Math.cos(bearing), s = Math.sin(bearing);
  // Fit in the model's viewing orientation, not the much larger north-up
  // bounding rectangle around a diagonal city block.
  const rotate = (point: number[], inverse = false) => {
    const [x, y] = mercator(point), dx = x - anchor[0], dy = y - anchor[1];
    const sign = inverse ? -1 : 1;
    return lngLat([anchor[0] + dx * c + sign * dy * s,
      anchor[1] - sign * dx * s + dy * c]);
  };
  const points = view.footprint.flatMap(point => {
    const ground = rotate(point);
    return [ground, [ground[0], ground[1] + roofOffset / metresPerDegree]];
  });
  const bounds: [[number, number], [number, number]] = [
    [Math.min(...points.map(p => p[0])), Math.min(...points.map(p => p[1]))],
    [Math.max(...points.map(p => p[0])), Math.max(...points.map(p => p[1]))],
  ];
  const padding = modelFocusPadding(container.clientWidth, container.clientHeight, coveredLeft);
  const camera = map.cameraForBounds(bounds, { padding, bearing: 0, pitch, maxZoom: 21 });
  if (!camera?.center) return false;
  const center = camera.center;
  const virtualCenter = Array.isArray(center) ? center :
    ['lng' in center ? center.lng : center.lon, center.lat];
  camera.center = rotate(virtualCenter, true);
  camera.bearing = view.bearing;
  // Extra breathing room for perspective-expanded near corners and tree crowns.
  camera.zoom = Math.max(0, (camera.zoom ?? 0) - 0.12);
  // Padding is used to calculate the destination, without persisting it into
  // later user gestures or route fits. No continuous selection-follow loop.
  map.easeTo({ ...camera, padding, retainPadding: false, duration: 1000, essential: false });
  return true;
}
